/**
 * `item_id` → **Customize Item ID column** (spec M2 tier-2 §5.13, card P-123).
 *
 * The capture stops at the row: `editors/item_id/settings-dialog.json` is the `Settings` submenu,
 * whose first entry is *Customize Item ID column*, and the dialog behind it was never opened. Its
 * fields are therefore taken from `itemIdSettingsSchema`, which is monday's documented shape
 * `[09§A.7 item_id]`:
 *
 * | key | values | effect |
 * |---|---|---|
 * | `displayType` | `pulseId` · `customKey` | the raw item id, or a prefixed and left-padded key |
 * | `customKeySettings` | `{prefix, padding}` | only meaningful under `customKey` |
 * | `copyValueSetting` | `text` · `link` | what the cell's copy button puts on the clipboard |
 *
 * The **fragment always keeps the raw id** whatever this says (spec §5.13), so nothing here can
 * change a value — only what the grid paints and what the copy button writes.
 */
import type { ItemIdSettings } from '@elizmonday/shared';
import {
  HideSummaryField,
  SegmentedField,
  SettingsField,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

const DISPLAY = [
  { id: 'pulseId', label: 'Item ID', value: 'pulseId' },
  { id: 'customKey', label: 'Custom key', value: 'customKey' },
] as const;

const COPY = [
  { id: 'text', label: 'The id', value: 'text' },
  { id: 'link', label: 'A link to the item', value: 'link' },
] as const;

export function ItemIdSettingsDialog({ settings, onCommit, onCancel }: ColumnSettingsProps<ItemIdSettings>) {
  const [draft, patch] = useSettingsDraft<ItemIdSettings>(settings);
  const custom = draft.customKeySettings ?? {};
  const patchCustom = (next: { prefix?: string; padding?: number }) =>
    patch({ customKeySettings: { ...custom, ...next } });

  return (
    <SettingsForm
      title="Item ID column settings"
      testId="item-id-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SegmentedField
        label="Display"
        testId="item-id-display"
        value={draft.displayType ?? 'pulseId'}
        options={DISPLAY}
        onChange={(next) => patch({ displayType: next })}
      />
      {draft.displayType === 'customKey' ? (
        <>
          <SettingsField
            label="Prefix"
            testId="item-id-prefix"
            value={custom.prefix ?? ''}
            placeholder="TASK-"
            maxLength={16}
            onChange={(next) => patchCustom({ prefix: next })}
          />
          <SettingsField
            label="Pad to"
            testId="item-id-padding"
            type="number"
            inline
            min={0}
            max={12}
            value={String(custom.padding ?? 0)}
            onChange={(next) => patchCustom({ padding: Number(next) })}
          />
        </>
      ) : null}
      <SegmentedField
        label="Copy value as"
        testId="item-id-copy"
        value={draft.copyValueSetting ?? 'text'}
        options={COPY}
        onChange={(next) => patch({ copyValueSetting: next })}
      />
      <HideSummaryField
        testId="item-id-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
